import { useContext, useEffect } from "react";
import { useWebClientApi } from "src/api";
import { UserContext, useAuth } from "src/context";
import type { UserDataProps } from "src/types";
import {
  dealerStorage,
  getCoBorrowerIdsFromApplication,
  getMainBorrowerIdFromApplication,
} from "src/utils";
import { useApiErrorHandler } from "./useApiErrorHandler";

export const useRoleCacheRefresh = () => {
  const { userData, setUserData } = useContext(UserContext);
  const { user } = useAuth();
  const api = useWebClientApi();
  const { handleApiError } = useApiErrorHandler();

  useEffect(() => {
    if (
      !user ||
      !userData?.application_id ||
      isBorrowerRoleCurrentlyTracked(userData)
    ) {
      return;
    }

    const fetchRoles = async () => {
      try {
        const application = await api.getApplication();
        const borrower = getMainBorrowerIdFromApplication(application);
        const coborrowers = getCoBorrowerIdsFromApplication(application);
        setUserData?.((prev) => ({
          ...prev,
          /**
           * On a fresh login, if borrower_id is not set, default to main borrower.
           * In app flow will handle who the active borrower_id is as normal.
           */
          ...(prev?.borrower_id ? {} : { borrower_id: borrower }),
          roles: {
            borrower,
            coborrowers,
          },
        }));
        dealerStorage.removeItem("email-for-register");
        dealerStorage.removeItem("phone-for-register");
      } catch (err) {
        handleApiError(
          err instanceof Error ? err : new Error("Role cache refresh failed"),
        );
        console.log(err);
      }
    };

    void fetchRoles();
  }, [api, userData, user]);
};

const isBorrowerRoleCurrentlyTracked = (userData: UserDataProps) =>
  userData.borrower_id &&
  (userData.borrower_id === userData.roles?.borrower ||
    userData.roles?.coborrowers?.includes(userData.borrower_id));
